import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Fade,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  setOverlayChromePrimed,
  useThemeColorOverlay,
} from '../../hooks/use-theme-color/use-theme-color';
import { Button } from '../button/button';
import { SteppedProgress } from '../stepped-progress';
import { MODAL_TRANSITION_TIMEOUT, modalDropInSx } from './modal-transition';

export interface IMultiStepActions<TData = Record<string, unknown>> {
  next: () => void;
  back: () => void;
  goToStep: (stepIndex: number) => void;
  updateData: (updates: Partial<TData>) => void;
  getData: () => TData;
  getCurrentStep: () => number;
  getTotalSteps: () => number;
  close: () => void;
}

export interface IMultiStepModalStep<TData = Record<string, unknown>> {
  title: string | ReactNode;
  body: (data: TData, actions: IMultiStepActions<TData>) => ReactNode;
  canGoNext?: (data: TData) => boolean;
  canGoBack?: boolean;
  hideBackButton?: boolean;
  hideNextButton?: boolean;
  /**
   * Static label, or a function of the current data when the label depends on
   * state (e.g. a Review step that reads "Next" while a later step is visible
   * and "Submit" once that step is hidden).
   */
  nextButtonText?: string | ((data: TData) => string);
  backButtonText?: string;
  /**
   * Conditionally include this step based on the current data. A step that
   * returns `false` is skipped during navigation and omitted from the progress
   * indicator, so it can appear/disappear as the user edits earlier steps.
   * Steps without a predicate are always visible (unchanged behavior).
   */
  visible?: (data: TData) => boolean;
  onNext?: (data: TData) => void | Promise<void>;
  onBack?: (data: TData) => void | Promise<void>;
}

export interface IMultiStepModal<TData = Record<string, unknown>> {
  title?: string | ReactNode;
  steps: IMultiStepModalStep<TData>[];
  initialData?: TData;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Force fullscreen at every breakpoint. */
  fullScreen?: boolean;
  /**
   * Go fullscreen only on small screens (< sm), staying centered on larger.
   *
   * Defaults ON: a multi-step modal is a wizard — step content, a progress
   * indicator and a footer of actions — and there is no phone on which that
   * fits comfortably inside a centered card. Pass `false` for the rare short
   * one that should stay a card.
   */
  fullScreenOnMobile?: boolean;
  disableBackdropClick?: boolean;
  onComplete?: (data: TData) => void | Promise<void>;
  onCancel?: (data: TData) => void;
  dialogProps?: Partial<DialogProps>;
  /**
   * Accessible name for a wizard with no title bar. It goes on the paper — the
   * element with role="dialog" — because an `aria-label` passed through
   * `dialogProps` lands on the presentation root, where nothing reads it.
   */
  ariaLabel?: string;
  /** Show default stepped progress indicator. Default: true */
  showProgress?: boolean;
  /** Custom progress renderer. Overrides showProgress if provided */
  renderProgress?: (currentStep: number, totalSteps: number) => ReactNode;
  /** Optional error handler for step progression failures */
  onError?: (error: Error, context: 'next' | 'back' | 'complete') => void;
  /**
   * Render a Cancel button on the left of the footer when on the first step
   * (where the Back button doesn't apply). Useful when the title/close-X is
   * suppressed (e.g. fullscreen mobile) so the user still has an exit.
   */
  showCancelOnFirstStep?: boolean;
  /** Text for the Cancel button (default 'Cancel'). */
  cancelButtonText?: string;
}

interface IMultiStepModalRendererProps<TData = Record<string, unknown>> {
  modal: IMultiStepModal<TData>;
  onClose: () => void;
}

// Context to share data and actions across step components
// Context must hold IMultiStepActions<TData> for any TData; `unknown` breaks
// structural assignability of the Provider's `value` prop across the generic
// parameter, so this stays `any` (narrowed back to TData in useMultiStepModal).
const MultiStepContext = createContext<IMultiStepActions<any> | null>(null);

export const useMultiStepModal = <TData = Record<string, unknown>,>() => {
  const context = useContext(MultiStepContext);
  if (!context) {
    throw new Error('useMultiStepModal must be used within a MultiStepModal');
  }
  return context as IMultiStepActions<TData>;
};

export default function MultiStepModalRenderer<
  TData = Record<string, unknown>,
>({ modal, onClose }: IMultiStepModalRendererProps<TData>) {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<TData>(modal.initialData || ({} as TData));
  const [isLoading, setIsLoading] = useState(false);

  const muiTheme = useTheme();
  const isMobileScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const fullScreen =
    modal.fullScreen || ((modal.fullScreenOnMobile ?? true) && isMobileScreen);

  // Always registered while mounted; only fullscreen names a colour — see
  // modal-renderer for why presence and colour are tracked separately.
  useThemeColorOverlay(
    true,
    fullScreen ? muiTheme.palette.background.paper : undefined,
  );

  // Dim the chrome for as long as we're mounted, and release on unmount. This
  // renderer is mounted directly rather than through the provider's setModal,
  // so nothing else primes or un-primes on our behalf.
  useEffect(() => {
    setOverlayChromePrimed(true);
    return () => setOverlayChromePrimed(false);
  }, []);

  // Synchronous in-flight guard. `isLoading` is React state, so the footer
  // button's `disabled={isLoading}` only takes effect after a re-render — two
  // clicks (or Enter-then-click) dispatched in the same tick both enter
  // `next()` before the disable commits, double-firing `onComplete`. For a
  // step whose completion CREATES data (e.g. processorSchedulingCreate) that
  // double-fire persists a full duplicate record set. This ref is set/checked
  // synchronously so the second call bails immediately.
  const inFlightRef = useRef(false);
  const prevStepRef = useRef(currentStep);

  // Update previous step reference after render
  useEffect(() => {
    prevStepRef.current = currentStep;
  }, [currentStep]);

  // Calculate slide direction based on previous step
  const slideDirection: 'left' | 'right' =
    currentStep > prevStepRef.current ? 'left' : 'right';

  // Original indices of the steps currently visible for this data. Steps with a
  // `visible` predicate that returns false drop out of navigation and the
  // progress bar, so the flow length reacts to what the user has entered.
  const visibleStepIndices = useMemo(
    () =>
      modal.steps
        .map((step, index) =>
          !step.visible || step.visible(data) ? index : -1,
        )
        .filter((index) => index >= 0),
    [modal.steps, data],
  );

  // If the step we're on becomes invisible (e.g. the user went back and changed
  // an answer that hides it), snap to the nearest still-visible step at or
  // before it so we never render a hidden step.
  useEffect(() => {
    if (
      visibleStepIndices.length > 0 &&
      !visibleStepIndices.includes(currentStep)
    ) {
      const fallback =
        [...visibleStepIndices].reverse().find((i) => i <= currentStep) ??
        visibleStepIndices[0];
      setCurrentStep(fallback);
    }
  }, [visibleStepIndices, currentStep]);

  const currentStepConfig = modal.steps[currentStep];
  const currentVisiblePosition = visibleStepIndices.indexOf(currentStep);
  const isFirstStep = currentVisiblePosition <= 0;
  const isLastStep = currentVisiblePosition === visibleStepIndices.length - 1;

  const updateData = useCallback((updates: Partial<TData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  const getData = useCallback(() => data, [data]);

  const getCurrentStep = useCallback(() => currentStep, [currentStep]);

  const getTotalSteps = useCallback(
    () => modal.steps.length,
    [modal.steps.length],
  );

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < modal.steps.length) {
        setCurrentStep(stepIndex);
      }
    },
    [modal.steps.length],
  );

  const next = useCallback(async () => {
    // Bail synchronously if a step transition (which may run onComplete's
    // create) is already in flight — see `inFlightRef` above.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      if (currentStepConfig.onNext) {
        await currentStepConfig.onNext(data);
      }

      const position = visibleStepIndices.indexOf(currentStep);
      const nextStepIndex = visibleStepIndices[position + 1];
      if (nextStepIndex === undefined) {
        if (modal.onComplete) {
          await modal.onComplete(data);
        }
        onClose();
      } else {
        setCurrentStep(nextStepIndex);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in step progression:', error);
      if (modal.onError) {
        modal.onError(
          error instanceof Error ? error : new Error(String(error)),
          isLastStep ? 'complete' : 'next',
        );
      }
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [
    currentStepConfig,
    data,
    isLastStep,
    modal,
    onClose,
    visibleStepIndices,
    currentStep,
  ]);

  const back = useCallback(async () => {
    setIsLoading(true);
    try {
      if (currentStepConfig.onBack) {
        await currentStepConfig.onBack(data);
      }
      const position = visibleStepIndices.indexOf(currentStep);
      const prevStepIndex = visibleStepIndices[position - 1];
      if (prevStepIndex !== undefined) {
        setCurrentStep(prevStepIndex);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error going back:', error);
      if (modal.onError) {
        modal.onError(
          error instanceof Error ? error : new Error(String(error)),
          'back',
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentStepConfig, data, modal, visibleStepIndices, currentStep]);

  // The close button, the backdrop, Escape and the first step's Cancel all come
  // through here. None of them may dismiss the wizard while a step transition
  // is in flight: the last one runs `onComplete`, and closing over that leaves
  // (say) a customer merge running with no dialog to report how it ended.
  // `actions.close()` and the close after a successful `onComplete` call
  // `onClose` directly, so they are unaffected.
  const handleClose = useCallback(() => {
    if (inFlightRef.current) return;
    if (modal.onCancel) {
      modal.onCancel(data);
    }
    onClose();
  }, [modal, data, onClose]);

  const canGoNext = useMemo(() => {
    if (currentStepConfig.canGoNext) {
      return currentStepConfig.canGoNext(data);
    }
    return true;
  }, [currentStepConfig, data]);

  const canGoBack = useMemo(() => {
    if (currentStepConfig.canGoBack !== undefined) {
      return currentStepConfig.canGoBack;
    }
    return !isFirstStep;
  }, [currentStepConfig, isFirstStep]);

  const actions: IMultiStepActions<TData> = useMemo(
    () => ({
      next,
      back,
      goToStep,
      updateData,
      getData,
      getCurrentStep,
      getTotalSteps,
      close,
    }),
    [
      next,
      back,
      goToStep,
      updateData,
      getData,
      getCurrentStep,
      getTotalSteps,
      close,
    ],
  );

  // The first step renders statically — only animate the slide once the user
  // has navigated between steps. `prevStepRef` (updated in an effect after
  // render) still holds the previous step during the render where currentStep
  // changes, so a mismatch here means a navigation just happened.
  const hasNavigatedRef = useRef(false);
  if (currentStep !== prevStepRef.current) {
    hasNavigatedRef.current = true;
  }

  let progressContent: ReactNode = null;
  if (modal.renderProgress) {
    progressContent = (
      <Box sx={{ px: 5, py: 1 }}>
        {modal.renderProgress(
          Math.max(0, currentVisiblePosition) + 1,
          visibleStepIndices.length,
        )}
      </Box>
    );
  } else if (modal.showProgress !== false) {
    progressContent = (
      <Box
        sx={
          modal.title
            ? { pt: 0, px: 1, pb: 1 }
            : {
                p: 0,
                // SteppedProgress applies its own 4px top/bottom padding;
                // strip it so the chevron sits flush as a true header.
                '& [role="progressbar"]': { padding: 0 },
                // Headerless + fullscreen makes this the topmost element, so it
                // inherits the DialogTitle's job of clearing the notch. Padding
                // sits on the wrapper so the chevrons stay flush to the edges
                // (Safari reports a 0 inset, making this a no-op there).
                ...(fullScreen && { pt: 'env(safe-area-inset-top)' }),
              }
        }
      >
        <SteppedProgress
          steps={visibleStepIndices.map((index) => {
            const { title } = modal.steps[index];
            return typeof title === 'string' ? title : 'Step';
          })}
          currentStep={Math.max(0, currentVisiblePosition) + 1}
          rounded={Boolean(modal.title)}
          // Headerless: square-cornered and full-bleed so it reads as the
          // dialog's own header bar, at the same 36px the cutsheet catalogue
          // wizard uses. With a title it's an in-content indicator, so it stays
          // rounded and short.
          height={modal.title ? 24 : 36}
          compact={isMobileScreen}
        />
      </Box>
    );
  }

  return (
    <MultiStepContext.Provider value={actions}>
      <Dialog
        open
        onClose={modal.disableBackdropClick ? undefined : handleClose}
        maxWidth={modal.size || 'md'}
        fullWidth
        fullScreen={fullScreen}
        // Quick fade in + a subtle 20px drop.
        slots={{ transition: Fade }}
        slotProps={{
          transition: { timeout: MODAL_TRANSITION_TIMEOUT },
          paper: {
            // See modal-renderer: the provider owns its own mobile sizing, so
            // it opts out of the theme's full-bleed-on-mobile rule.
            className: fullScreen ? undefined : 'fs-dialog-compact',
            sx: modalDropInSx,
            ...(modal.ariaLabel && { 'aria-label': modal.ariaLabel }),
          },
        }}
        {...modal.dialogProps}
      >
        {modal.title && (
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 0.5,
              // Fullscreen means the paper runs edge to edge, so the title is
              // under the status bar / notch when installed to the home screen
              // (in Safari the inset is 0 and this is a no-op).
              ...(fullScreen && { pt: 'calc(env(safe-area-inset-top) + 4px)' }),
            }}
          >
            {modal.title}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              disabled={isLoading}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
        )}
        {progressContent}

        <DialogContent
          sx={{
            minHeight: 200,
            overflow: 'scroll',
            position: 'relative',
            pt: 1,
            pb: 1,
            // MUI's default 24px horizontal padding wastes too much room on
            // small/full-screen surfaces; tighten when fullScreen.
            ...(fullScreen && { px: 1.5 }),
          }}
        >
          <Slide
            direction={slideDirection}
            in
            appear={hasNavigatedRef.current}
            mountOnEnter
            unmountOnExit
            key={currentStep}
          >
            <Box>{currentStepConfig.body(data, actions)}</Box>
          </Slide>
        </DialogContent>

        <DialogActions
          sx={{
            // Same edge-to-edge problem at the bottom: Back/Next would sit under
            // the iOS home indicator (and behind Safari's toolbar) without the
            // inset. 8px is MUI's own DialogActions padding, kept on top of it.
            ...(fullScreen && {
              px: 1.5,
              pb: 'calc(env(safe-area-inset-bottom) + 8px)',
            }),
          }}
        >
          {modal.showCancelOnFirstStep && isFirstStep && (
            <Button
              content={modal.cancelButtonText || 'Cancel'}
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
              sx={{ mr: 'auto' }}
            />
          )}

          {!currentStepConfig.hideBackButton && canGoBack && (
            <Button
              content={currentStepConfig.backButtonText || 'Back'}
              variant="ghost"
              onClick={back}
              disabled={isLoading}
            />
          )}

          {!currentStepConfig.hideNextButton && (
            <Button
              content={
                (typeof currentStepConfig.nextButtonText === 'function'
                  ? currentStepConfig.nextButtonText(data)
                  : currentStepConfig.nextButtonText) ||
                (isLastStep ? 'Complete' : 'Next')
              }
              variant="primary"
              onClick={next}
              disabled={!canGoNext || isLoading}
              isLoading={isLoading}
            />
          )}
        </DialogActions>
      </Dialog>
    </MultiStepContext.Provider>
  );
}
